import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  Suspense,
} from "react";

import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import { Switch, useLocation, useHistory } from "react-router-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { AttentionSeeker } from "react-awesome-reveal";

import clsx from "clsx";

import AcDashboard from "@views/ac-dashboard/ac-dashboard";

// Imports => Config
import config from "@config";

import { AcTokenRefresher } from "@api/interceptors.api";

// Imports => Assets
import OPP_LOGO from "@assets/images/menu-logo-expanded.svg";
import OPP_LOGO_COLLAPSED from "@assets/images/menu-logo-collapsed.svg";

// Imports => Constants
import { AcIsArray, AcSaveState, ENVIRONMENTS, ICONS, KEYS } from "@opp/ui-kit";
import { AUTHENTICATION_ROUTES, DEFAULT_ROUTE, ROUTES } from "@constants";

// Imports => Utilities
import { AcIsSet, AcIsString, AcIsEmptyString } from "@opp/ui-kit";

// Imports => Hooks
import { useFormActions } from "@hooks";

// Imports => Molecules
import AcModal from "@molecules/ac-modal/ac-modal";
import AcMerchantSelectModal from "@molecules/ac-merchant-select-modal/ac-merchant-select-modal";
import AcConfirmationModal from "@molecules/ac-confirmation-modal/ac-confirmation-modal";
import AcTimeoutNoticeModal from "@molecules/ac-timeout-notice-modal/ac-timeout-notice-modal";
import AcNoInternetConnectionNoticeModal from "@molecules/ac-no-internet-connection-notice-modal/ac-no-internet-connection-notice-modal";
import AcKeyboardShortcutsModal from "@molecules/ac-keyboard-shortcuts-modal/ac-keyboard-shortcuts-modal";

// Imports => Components
import AcHeader from "@components/ac-header/ac-header";
import AcTwoFactorNotice from "@components/ac-two-factor-notice/ac-two-factor-notice";
import AcComplianceRequirementNotice from "@components/ac-compliance-requirement-notice/ac-compliance-requirement-notice";
import AcPendingComplianceRequirementNotice from "@components/ac-pending-compliance-requirement-notice/ac-pending-compliance-requirement-notice";
import AcProfileSwitcher from "@components/ac-profile-switcher/ac-profile-switcher";
import AcNavigation from "@components/ac-navigation/ac-navigation";
import AcSubNavigation from "@components/ac-sub-navigation/ac-sub-navigation";
import AcSupportCenter from "@components/ac-support-center/ac-support-center";
import AcMessageCenter from "@components/ac-message-center/ac-message-center";
import AcActivityMonitor from "@components/ac-activity-monitor/ac-activity-monitor";

// Imports => Atoms
import AcScrollHOC from "@atoms/ac-scroll-hoc/ac-scroll-hoc";
import {
  AcPrivateRoute,
  AcContainer,
  AcRow,
  AcColumn,
  AcIcon,
  AcIconButton,
  AcToasterHoc,
  AcOffCanvas,
  AcBackdrop,
} from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import { setLocale } from "./config/moment";
import AcSearchBox from "./components/ac-search-box/ac-search-box";
import { useErrorBoundary } from "use-error-boundary";
import AcNavigationError from "./components/error-boundaries/ac-navigation-error";
import AcDefaultError from "./components/error-boundaries/ac-default-error";
import { useAnalytics } from "use-analytics";
import moment from "moment";
import { useAppDispatch, useAppSelector, useIsAuthorized } from "./rtk/hooks";
import { useLazyRefreshAuthQuery } from "./api/openapi/empty";
import { handleAppAuthentication, handleLogout } from "./rtk/slices/authSlice";
import { registerLocale } from "react-datepicker";
import nl from "date-fns/locale/nl";
import de from "date-fns/locale/de";
import fr from "date-fns/locale/fr";
import { StringParam, useQueryParam } from "use-query-params";
registerLocale("nl", nl);
registerLocale("de", de);
registerLocale("fr", fr);

const _CLASSES = {
  ROOT: "ac-root",
  MAIN: "ac-app",
  NAVIGATION_VISIBLE: "ac-app--navigation-visible ac-app--off-canvas-visible",
  BACKDROP_VISIBLE: "ac-search-box__backdrop--visible",
  NOTICE: "ac-app--notice",
  ROUTE: {
    SECTION: "ac-route__section",
  },
  INSET_BADGE: "ac-header__inset-badge",
};

const Header = ({
  query,
  handleSearchInputChange,
  handleSearchClose,
  global,
  hasNotice,
  children,
}) => {
  return (
    <AcHeader className={"ac-header"}>
      <AcContainer fluid>
        <AcRow>
          <AcColumn
            xxs={6}
            xs={6}
            sm={6}
            md={4}
            lg={3}
            className={"ac-search-column"}
          >
            <AcSearchBox
              name={"query"}
              searchValue={query}
              callback={handleSearchInputChange}
              close={handleSearchClose}
              hasNotice={hasNotice}
            />
          </AcColumn>
          <AcColumn
            xxs={6}
            xs={6}
            sm={6}
            md={8}
            lg={9}
            className={"h-flex-h-align-end h-text--align-right"}
          >
            {/*<AcIconButton*/}
            {/*   icon={ICONS.BELL_OUTLINE}*/}
            {/*   callback={handleToggleMessageCenter}*/}
            {/*   style={{ zIndex: 200000 }}*/}
            {/*   title={TITLES.MESSAGES_AND_NOTIFICATIONS}*/}
            {/*/>*/}

            {children}
          </AcColumn>
        </AcRow>
      </AcContainer>
    </AcHeader>
  );
};
const App = ({
  store: { auth, global, profile, ui, toasters, balance, settings, api },
  i18n,
  t,
}) => {
  const dispatch = useAppDispatch();
  const [trigger] = useLazyRefreshAuthQuery();
  const [query, setQuery] = useQueryParam("addBalanceStatus", StringParam);

  useEffect(() => {
    if (["pending", "completed", "reserved"].includes(query)) {
      toasters.add({
        variant: "success",
        title: t(`transactions.add-balance-${query}`, { ns: "store" }),
      });
    } else if (["failed", "cancelled"].includes(query)) {
      toasters.add({
        variant: "error",
        title: t(`transactions.add-balance-${query}`, { ns: "store" }),
      });
    } else if (query) {
      toasters.add({
        variant: "error",
        title: t("transactions.add-balance-failed", { ns: "store" }),
      });
    }
    setQuery(null);
  }, [query, toasters]);
  const expires_at = useAppSelector((state) => state.auth.expires_at);
  useEffect(() => {
    // refresh trigger when expire time is close
    const duration = moment.duration(
      moment(~~expires_at * 1000).diff(moment())
    );

    const expiresInMins = duration.asMinutes();
    if (expiresInMins < 7 && expiresInMins > 0) trigger(null);
    else if (expires_at) {
      const timeout = setTimeout(() => {
        trigger(null);
      }, duration.subtract(7, "minutes").asMilliseconds());
      return () => clearTimeout(timeout);
    }
  }, [expires_at, trigger]);
  window.addEventListener("unAuthenticate", () => {
    dispatch(handleLogout());
  });
  window.addEventListener("handleAuthentication", (account) => {
    dispatch(handleAppAuthentication(account.detail));
  });
  const { track, page } = useAnalytics();
  const { currentPermissions } = useAppSelector((state) => state.auth);
  const isAuthorized = useIsAuthorized();

  const location = useLocation();

  useEffect(() => {
    page(location.pathname + location.search);
  }, [location]);

  const { ErrorBoundary } = useErrorBoundary();

  const $monitor = useRef(null);
  const $navigation = useRef(null);
  const $nodeRef = useRef(null);

  const $searchDelay = useRef(null);

  const history = useHistory();

  const [isOnline, setIsOnline] = useState(navigator && navigator.onLine);

  const [modalTimeOpened, setModalTimeOpened] = useState(false);
  const [modalTitle, setModalTitle] = useState(false);

  const [fields, setFields] = useState({
    query: "",
  });
  const { handleInputChange: handleSearchInputChange } = useFormActions({
    t,
    fields,
    setFields,
  });

  const { current_profile, current_profile_uid, current_merchant_profiles } =
    profile;

  const {
    has_unverified_compliance_requirement,
    has_pending_compliance_requirement,
    has_open_tickets,
    has_purchases,
    configurations
  } = settings;

  const handleSearchClose = () => {
    global.reset(KEYS.SEARCH_RESULTS);
  };

  const twoFactor = useMemo(() => {
    if (!AcIsSet(current_profile)) {
      return false;
    }

    const { two_factor } = current_profile;

    return two_factor;
  }, [current_profile.two_factor]);

  const hasUnverifiedComplianceRequirement = useMemo(() => {
    if (!AcIsSet(has_unverified_compliance_requirement)) {
      return false;
    }

    return has_unverified_compliance_requirement;
  }, [has_unverified_compliance_requirement]);

  const hasPendingComplianceRequirement = useMemo(() => {
    if (!AcIsSet(has_pending_compliance_requirement)) {
      return false;
    }

    return has_pending_compliance_requirement;
  }, [has_pending_compliance_requirement]);

  const hasNotice = useMemo(() => {
    if (settings.is_loading) {
      return;
    }

    return (
      !twoFactor ||
      hasUnverifiedComplianceRequirement ||
      hasPendingComplianceRequirement
    );
  }, [
    hasUnverifiedComplianceRequirement,
    hasPendingComplianceRequirement,
    twoFactor,
    settings.is_loading,
  ]);

  const hasOpenTickets = useMemo(() => {
    if (!AcIsSet(has_open_tickets)) {
      return false;
    }

    return has_open_tickets;
  }, [has_open_tickets]);

  const authorized = useMemo(() => {
    const notAnAuthorizationRoute =
      AUTHENTICATION_ROUTES.indexOf(location.pathname) === -1;

    return isAuthorized && notAnAuthorizationRoute;
  }, [isAuthorized, location]);

  useEffect(() => {
    const $meta = document.querySelector('meta[name="theme-color"]');
    if ($meta) {
      if (hasUnverifiedComplianceRequirement && configurations.kyc) {
        $meta.setAttribute("content", "#fa5252");
      } else if (
        (!twoFactor && authorized) ||
          (hasPendingComplianceRequirement && configurations.kyc)
      ) {
        $meta.setAttribute("content", "#4642F0");
      } else {
        $meta.setAttribute("content", "#edf2f7");
      }
    }
  }, [
    authorized,
    twoFactor,
    hasUnverifiedComplianceRequirement,
    hasPendingComplianceRequirement,
  ]);

  useEffect(() => {
    handleRouteChanged();
    addEvents();

    return () => removeEvents();
  }, [location.pathname]);

  useEffect(() => {
    if (!isOnline) {
      displayNoInternetConnectionNoticeModal();
    } else {
      ui.reset(KEYS.MODAL).then(() => {
        // window.location.reload(false);
      });
    }
  }, [isOnline]);

  useEffect(() => {
    if (authorized) {
      profile.who_am_i(t).then((response) => {
        if (response && response.language) {
          let savedLanguage = response.language;
          AcSaveState(KEYS.LOCALE, savedLanguage);

          const resolvedLanguage = i18n.resolvedLanguage;

          if (resolvedLanguage !== i18n.resolvedLanguage) {
            setLocale(resolvedLanguage);
          }
        }
      });

      profile.list(t);
      balance.get(t);

      settings.get(t, { locale: i18n.resolvedLanguage.slice(0, 2) });
    }
  }, [authorized]);

  const hasProfiles = useMemo(() => {
    if (!isAuthorized) {
      return;
    }
    if (!AcIsSet(current_merchant_profiles)) {
      return;
    }

    const { merchant, merchants } = current_merchant_profiles;

    if (!AcIsSet(merchant) || !AcIsSet(merchants) || !AcIsArray(merchants)) {
      return false;
    }
    const filtered =
      merchants && merchants.filter((item) => item.uid !== merchant.uid);

    return AcIsSet(filtered) && AcIsArray(filtered) && filtered.length > 0;
  }, [current_merchant_profiles]);

  const handleHideUIElements = () => {
    // Reset UI Elements
    ui.setValue(KEYS.SUB_NAVIGATION, KEYS.VISIBLE, false);
    ui.setValue(KEYS.MESSAGE_CENTER, KEYS.VISIBLE, false);
    ui.setValue(KEYS.SUPPORT_CENTER, KEYS.VISIBLE, false);
    ui.setValue(KEYS.DRAWER, KEYS.VISIBLE, false);

    // Reset global search
    global.reset(KEYS.SEARCH_RESULTS);
    handleSearchInputChange(null, "query", "");

    // Reset modal (if present)
    if (
      ui.current_modal &&
      ui.current_modal.type &&
      ui.current_modal.type !== "timeout"
    ) {
      ui.reset(KEYS.MODAL);
    }
  };

  useEffect(() => {
    if ($searchDelay.current) {
      clearTimeout($searchDelay.current);
    }
    if (
      AcIsSet(fields.query) &&
      AcIsString(fields.query) &&
      !AcIsEmptyString(fields.query) &&
      fields.query.length >= 3
    ) {
      $searchDelay.current = setTimeout(() => {
        global.search(t, fields.query);
      }, 400);
    } else {
      global.resetParams();
    }
  }, [fields, fields.query]);

  const isAuthRoute = useMemo(() => {
    const result = AUTHENTICATION_ROUTES.find(
      (item) => location.pathname.indexOf(item) > -1
    );

    return result;
  }, [location]);

  const handleRouteChanged = () => {
    handleHideUIElements();

    if (isAuthorized && !isAuthRoute) {
      if ($monitor && $monitor.current) {
        $monitor.current.restart();
      }
      return;
    }

    if ($monitor && $monitor.current) {
      $monitor.current.stop();
    }
  };

  const handleInternetConnectionChanged = () => {
    if (navigator && AcIsSet(navigator.onLine)) {
      setIsOnline(navigator.onLine);
    }
  };

  const addEvents = () => {
    document.addEventListener("keyup", handleKeyUp, { passive: true });
    document.addEventListener(
      "showKeyboardShortcuts",
      displayKeyboardShortcuts,
      { passive: true }
    );
    window.addEventListener("online", handleInternetConnectionChanged, {
      passive: true,
    });
    window.addEventListener("offline", handleInternetConnectionChanged, {
      passive: true,
    });
  };

  const removeEvents = () => {
    document.removeEventListener("keyup", handleKeyUp, { passive: true });
    document.removeEventListener(
      "showKeyboardShortcuts",
      displayKeyboardShortcuts,
      { passive: true }
    );
    window.removeEventListener("online", handleInternetConnectionChanged, {
      passive: true,
    });
    window.removeEventListener("offline", handleInternetConnectionChanged, {
      passive: true,
    });
  };

  const displayNoInternetConnectionNoticeModal = async (event) => {
    if (!isAuthorized) {
      return;
    }

    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.NO_INTERNET_CONNECTION", "No internet connection", {
        ns: "titles",
      })}`,
      name: "no-internet-connection-modal",
      body: <AcNoInternetConnectionNoticeModal />,
      closeable: false,
      centered: true,
      visible: true,

      callback: async () => {
        await ui.reset(KEYS.MODAL, KEYS.VISIBLE, false);
      },
    });
  };

  const displayMerchantSelectModal = async (event) => {
    if (!isAuthorized) {
      return;
    }
    if (!AcIsSet(current_merchant_profiles)) {
      return;
    }

    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    const { merchant, merchants } = current_merchant_profiles;

    const callback = () => {
      return new Promise(async (resolve) => {
        await ui.setValue(KEYS.MODAL, KEYS.CLOSEABLE, false);
        profile.who_am_i(t, true, true).then(async (response) => {
          if (response && response.language) {
            let lang = response.language;

            AcSaveState(KEYS.LOCALE, lang);
            setLocale(lang);
            i18n.changeLanguage(lang);
            setTimeout(() => {
              window.location.reload(false);
            }, 100);

          }

          let getBalance = new Promise((resolve) => {
            balance.get(t).then(async () => {
              resolve();
            });
          });

          let getSettings = new Promise((resolve) => {
            settings
              .get(t, { locale: i18n.resolvedLanguage.slice(0, 2) })
              .then(async () => {
                resolve();
              });
          });

          let listProfiles = new Promise((resolve) => {
            profile.list(t, true).then(async () => {
              resolve();
            });
          });

          await Promise.all([getBalance, getSettings, listProfiles]).then(
            async () => {
              history.replace(ROUTES.DASHBOARD.path);
              await ui.reset(KEYS.MODAL, KEYS.VISIBLE, false);
              resolve();
            }
          );
        });
      });
    };

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.SWITCH_MERCHANT", { ns: "titles" })}`,
      name: "switch-merchant-modal",
      body: (
        <AcMerchantSelectModal
          data={current_merchant_profiles}
          current={merchant}
          collection={merchants}
          callback={callback}
        />
      ),
      closeable: true,
      centered: false,
      visible: true,

      callback: async () => {
        await ui.reset(KEYS.MODAL, KEYS.VISIBLE, false);
      },
    });
  };

  const displayKeyboardShortcuts = async (event) => {
    if (!isAuthorized) {
      return;
    }

    if (event && (typeof event.cancelable !== "boolean" || event.cancelable)) {
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }
    }

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.KEYBOARD_SHORTCUTS", { ns: "titles" })}`,
      name: "keyboard-shortcuts-modal",
      body: <AcKeyboardShortcutsModal />,
      closeable: true,
      visible: true,

      callback: async () => {
        await ui.reset(KEYS.MODAL);
      },
    });
  };

  const displayTimeoutNoticeModal = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.SESSION_TIMEOUT", { ns: "titles" })}`,
      name: "session-timeout-modal",
      type: "timeout",
      body: (
        <AcTimeoutNoticeModal
          callback={async () => {
            await ui.reset(KEYS.MODAL);
            if ($monitor && $monitor.current) {
              $monitor.current.restart();
            }
            if (AcTokenRefresher) {
              AcTokenRefresher(null, () => dispatch(handleLogout()));
            }
          }}
          update={() => {
            ui.setValue(
              KEYS.MODAL,
              KEYS.TITLE,
              `${t("TITLES.SESSION_EXPIRED", { ns: "titles" })}`
            );
          }}
        />
      ),
      centered: true,
      closeable: false,
      visible: true,
      callback: () => {
        ui.reset(KEYS.MODAL);
      },
    });
  };

  const handleToggleNavigation = () => {
    if (
      AcIsSet($navigation) &&
      AcIsSet($navigation.current) &&
      AcIsSet($navigation.current.toggle)
    ) {
      $navigation.current.toggle();
    }
  };

  const handleToggleSupportCenter = (event, state = undefined) => {
    if (ui.support_center.visible) {
      ui.setValue(KEYS.SUPPORT_CENTER, KEYS.VISIBLE, false);
    } else {
      ui.setValue(KEYS.SUPPORT_CENTER, KEYS.VISIBLE, true);
    }
  };

  const handleToggleMessageCenter = (event, state = undefined) => {
    if (ui.message_center.visible) {
      ui.setValue(KEYS.MESSAGE_CENTER, KEYS.VISIBLE, false);
    } else {
      ui.setValue(KEYS.MESSAGE_CENTER, KEYS.VISIBLE, true);
    }
  };

  const handleToggleSubNavigation = (event, state = undefined) => {
    if (ui.support_center.visible) {
      ui.setValue(KEYS.SUB_NAVIGATION, KEYS.VISIBLE, false);
    } else {
      ui.setValue(KEYS.SUB_NAVIGATION, KEYS.VISIBLE, true);
    }
  };

  const handleKeyUp = (event) => {
    if (event && event.persist) {
      event.persist();
    }

    const key = event.key || event.which;

    const $active_element = document.activeElement;
    const $inputs = ["input", "select", "button", "textarea"];

    if (
      !$active_element ||
      $inputs.indexOf($active_element.tagName.toLowerCase()) === -1
    ) {
      if (key) {
        if (key === "?" || (key === 191 && shift)) {
          if (!ui.current_modal.visible) {
            displayKeyboardShortcuts();
          }
        } else if (key === "R" || (key === 82 && shift)) {
          AcTokenRefresher();
        } else {
          switch (key) {
            case "/":
            case 191:
            case "f":
            case 70:
              const $query = document.querySelector('input[name="query"]');
              if (AcIsSet($query) && $query.focus) {
                $query.focus();
              }
              break;

            case "[":
            case 219:
              if (!ui.current_modal.visible) {
                handleToggleNavigation();
              }
              break;

            case "]":
            case 221:
              if (!ui.current_modal.visible) {
                if (
                  AcIsSet($navigation) &&
                  AcIsSet($navigation.current) &&
                  AcIsSet($navigation.current.hide)
                ) {
                  $navigation.current.hide();
                }
              }
              ui.setValue(KEYS.SUB_NAVIGATION, KEYS.VISIBLE, false);
              ui.setValue(KEYS.MESSAGE_CENTER, KEYS.VISIBLE, false);
              ui.setValue(KEYS.SUPPORT_CENTER, KEYS.VISIBLE, false);
              break;

            case "c":
            case 67:
              if (!ui.current_modal.visible) {
                const $button = document.querySelector(
                  'button[rel="ac-add-button"]'
                );
                if (AcIsSet($button) && $button.click) {
                  $button.click();
                }
              }
              break;

            case "e":
            case 69:
              if (!ui.current_modal.visible) {
                const $edit = document.querySelector(
                  'i[class*="ac-details-card__edit"]'
                );
                if (AcIsSet($edit) && $edit.click) {
                  $edit.click();
                }
              }
              break;

            case "s":
              const $button = document.querySelector(
                'button[id="ac-profile-switcher-button"]'
              );
              if (AcIsSet($button) && $button.click) {
                $button.click();
              }
              break;

            default:
          }
        }
      }
    }
  };

  const getRouteSectionClassNames = useMemo(() => {
    return clsx(_CLASSES.ROUTE.SECTION);
  }, []);

  const getInsetBadgeClassNames = useMemo(() => {
    return clsx(_CLASSES.INSET_BADGE);
  });

  const getMainClassNames = useMemo(() => {
    return clsx(
      _CLASSES.MAIN,
      hasNotice && _CLASSES.NOTICE,
      ui.current_navigation.visible && _CLASSES.NAVIGATION_VISIBLE,
      ui.backdrop.visible && _CLASSES.BACKDROP_VISIBLE
    );
  }, [hasNotice, ui.current_navigation.visible, ui.backdrop.visible]);

  useEffect(() => {
    if (ui.current_modal.visible === true) {
      setModalTimeOpened(moment().valueOf());
      setModalTitle(ui.current_modal.name);
    }
    if (ui.current_modal.visible === false && modalTitle) {
      track(`time-spent-${modalTitle}`, {
        value: moment().valueOf() - modalTimeOpened,
      });

      setModalTimeOpened(false);
      setModalTitle(false);
    }
  }, [ui.current_modal.visible]);

  const renderModal = useCallback(() => {
    const { current_modal } = ui;
    let body = current_modal.body;

    return (
      <AcModal {...current_modal} callback={current_modal.callback}>
        {body}
      </AcModal>
    );
  }, [
    ui.current_modal,
    ui.current_modal.visible,
    ui.current_modal.closeable,
    ui.current_modal.title,
    ui.current_modal.body,
  ]);

  const renderConfirmModal = useCallback(() => {
    const { current_confirm_modal } = ui;

    return (
      <AcModal
        {...current_confirm_modal}
        callback={current_confirm_modal.callback}
      >
        <AcConfirmationModal {...current_confirm_modal.props} />
      </AcModal>
    );
  }, [
    ui.current_confirm_modal,
    ui.current_confirm_modal.visible,
    ui.current_confirm_modal.content,
    ui.current_confirm_modal.cancel,
    ui.current_confirm_modal.confirm,
  ]);

  const renderToasterHoc = useMemo(() => {
    return <AcToasterHoc queue={toasters.queue} callback={toasters.remove} />;
  }, [toasters, toasters.queue, toasters.collection]);

  const renderDefaultRoute = useMemo(() => {
    return (
      <AcPrivateRoute
        key={`default-route-${DEFAULT_ROUTE.id}`}
        component={AcDashboard}
        forbidden={DEFAULT_ROUTE.forbidden}
        authorized={isAuthorized}
        exact
      />
    );
  }, [isAuthorized]);

  const renderRoutes = useMemo(() => {
    const collection = ROUTES;
    let result = [];
    let key;

    for (key in collection) {
      const item = collection[key];
      const { forbidden, allowed } = item;

      if (forbidden && !isAuthorized) {
        continue;
      }

      if (
        !has_purchases &&
        (item.path === "/purchases" || item.path === "/purchases/:id")
      ) {
        continue;
      }

      const object = (
        <AcPrivateRoute
          key={`route-${item.id}`}
          authorized={isAuthorized}
          component={item.component}
          {...item}
        />
      );
      result.push(object);
    }

    return result;
  }, [isAuthorized, currentPermissions, has_purchases]);

  const renderBackdrop = useMemo(() => {
    const visible =
      ui.support_center.visible ||
      ui.message_center.visible ||
      ui.sub_navigation.visible;

    return (
      <AcBackdrop visible={visible} callback={handleHideUIElements} swipeable />
    );
  }, [
    ui.support_center.visible,
    ui.message_center.visible,
    ui.sub_navigation.visible,
    handleHideUIElements,
  ]);

  const renderEnvironmentIndicator = useMemo(() => {
    const { environment } = config;

    if (!AcIsSet(environment)) {
      return null;
    }

    let result = null;

    switch (environment) {
      case ENVIRONMENTS.DEV:
      case ENVIRONMENTS.DEVELOPMENT:
        result = (
          <div
            className={
              "ac-environment-indicator ac-environment-indicator--development"
            }
          >
            <AcIcon icon={ICONS.NINJA} />
            <span>DEVELOPMENT</span>
          </div>
        );
        break;

      case ENVIRONMENTS.TEST:
      case ENVIRONMENTS.ACCEPT:
      case ENVIRONMENTS.STAGING:
        result = (
          <div
            className={
              "ac-environment-indicator ac-environment-indicator--staging"
            }
          >
            <AcIcon icon={ICONS.NINJA} />
            <span>STAGING</span>
          </div>
        );
        break;

      case ENVIRONMENTS.SANDBOX:
      case "sandbox":
        result = (
          <div
            className={
              "ac-environment-indicator ac-environment-indicator--sandbox"
            }
          >
            <AcIcon icon={ICONS.NINJA} />
            <span>SANDBOX</span>
          </div>
        );
        break;

      case ENVIRONMENTS.LOCAL:
        result = (
          <div
            className={
              "ac-environment-indicator ac-environment-indicator--local"
            }
          >
            <AcIcon icon={ICONS.NINJA} />
            <span>LOCAL</span>
          </div>
        );
        break;

      default:
    }

    return result;
  }, []);

  const renderNavigation = useMemo(() => {
    return (
      <AcOffCanvas
        visible={ui.navigation.visible}
        callback={handleToggleNavigation}
        className={"ac-navigation--off-canvas"}
        id={"ac-navigation--off-canvas"}
        data-raised={false}
      >
        <div
          className={
            "ac-off-canvas__section ac-off-canvas__section--fullwidth h-text--align-center h-padding-x-15"
          }
        >
          <AcProfileSwitcher
            hasProfiles={hasProfiles}
            data={current_merchant_profiles}
            callback={displayMerchantSelectModal}
          />
        </div>

        <div
          className={"ac-off-canvas__section ac-off-canvas__section--fullwidth"}
        >
          <AcNavigation
            hasProfiles={hasProfiles}
            innerRef={$navigation}
            selectModalCallback={displayMerchantSelectModal}
          />
        </div>

        <div
          className={
            "ac-off-canvas__section ac-off-canvas__section--fullwidth ac-main-logo"
          }
          style={{ width: ui.navigation.visible ? "50%" : "80%" }}
        >
          <img src={ui.navigation.visible ? OPP_LOGO : OPP_LOGO_COLLAPSED} />
          {renderEnvironmentIndicator}
        </div>
      </AcOffCanvas>
    );
  }, [
    ui.navigation,
    renderEnvironmentIndicator,
    handleToggleNavigation,
    current_merchant_profiles,
  ]);

  const renderMessageCenter = useMemo(() => {
    return (
      <AcOffCanvas
        visible={ui.message_center.visible}
        callback={handleToggleMessageCenter}
        position={"right"}
        withToggle={false}
        wide
        raised
      >
        <AcMessageCenter />
      </AcOffCanvas>
    );
  }, [ui.message_center, handleToggleMessageCenter]);

  const renderSupportCenter = useMemo(() => {
    return (
      <AcOffCanvas
        visible={ui.support_center.visible}
        callback={handleToggleSupportCenter}
        position={"right"}
        withToggle={false}
        wide
        raised
      >
        <AcSupportCenter />
      </AcOffCanvas>
    );
  }, [ui.support_center, handleToggleSupportCenter]);

  const renderSubNavigation = useMemo(() => {
    return (
      <AcOffCanvas
        visible={ui.sub_navigation.visible}
        callback={handleToggleSubNavigation}
        position={"right"}
        withToggle={false}
        wide
        raised
      >
        <AcSubNavigation />
      </AcOffCanvas>
    );
  }, [ui.sub_navigation, handleToggleSubNavigation]);

  const renderNotice = useMemo(() => {
    if (settings.is_loading) {
      return;
    }

    if (authorized) {
      if (hasUnverifiedComplianceRequirement && configurations.kyc) {
        return (
          <AcComplianceRequirementNotice
            data={hasUnverifiedComplianceRequirement}
            key={"compliance-requirement-notice"}
          />
        );
      }

      if (AcIsSet(current_profile) && !twoFactor) {
        return (
          <AcTwoFactorNotice data={!twoFactor} key={"two-factor-notice"} />
        );
      }

      if (hasPendingComplianceRequirement && configurations.kyc) {
        return (
          <AcPendingComplianceRequirementNotice
            data={hasPendingComplianceRequirement}
            key={"pending-compliance-requirement-notice"}
          />
        );
      }
    }
  }, [
    authorized,
    twoFactor,
    hasUnverifiedComplianceRequirement,
    hasPendingComplianceRequirement,
    settings.is_loading,
    current_profile,
  ]);

  const SubNavigationButton = ({ current_merchant_profiles }) => {
    let icon = ICONS.OPP_USER_INDIVIDUAL;

    if (current_merchant_profiles?.merchant.type === "business") {
      icon = ICONS.OPP_USER_BUSINESS;
    }

    return (
      <div className={"ac-header__badge-wrap"}>
        <AttentionSeeker effect={"rubberBand"} duration={0} triggerOnce>
          <AcIconButton
            icon={icon}
            callback={handleToggleSubNavigation}
            style={{ zIndex: 200000 }}
            title={t("TITLES.ACCOUNT_AND_SETTINGS", { ns: "titles" })}
          />
        </AttentionSeeker>
        <span
          className={getInsetBadgeClassNames}
          style={{
            display: `${
                (hasUnverifiedComplianceRequirement && configurations.kyc) ? "inherit" : "none"
            }`,
          }}
        />
      </div>
    );
  };

  const SupportMenuButton = ({ bubble }) => {
    return (
      <div key="hasdata" className={"ac-header__badge-wrap"}>
        <AttentionSeeker effect={"rubberBand"} duration={0} triggerOnce>
          <AcIconButton
            icon={ICONS.SUPPORT}
            callback={handleToggleSupportCenter}
            style={{ zIndex: 200000 }}
            title={t("TITLES.SUPPORT_AND_TICKETS", { ns: "titles" })}
          />
        </AttentionSeeker>
        <span
          className={getInsetBadgeClassNames}
          style={{ display: `${bubble ? "inherit" : "none"}` }}
        />
      </div>
    );
  };

  const renderActivityMonitor = useMemo(() => {
    return (
      <AcActivityMonitor
        ref={$monitor}
        authorized={authorized}
        callback={displayTimeoutNoticeModal}
      />
    );
  }, [authorized, displayTimeoutNoticeModal]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ErrorBoundary
        renderError={({ error }) => <AcNavigationError error={error} />}
      >
        <AcScrollHOC>
          <main className={getMainClassNames}>
            <section id={KEYS.SCROLLER} className={getRouteSectionClassNames}>
              <TransitionGroup component={null}>
                <CSSTransition
                  key={location.pathname}
                  timeout={350}
                  classNames={"page-fade"}
                  nodeRef={$nodeRef}
                  unmountOnExit
                >
                  <div ref={$nodeRef}>
                    <Switch location={location}>
                      {renderRoutes}

                      {renderDefaultRoute}
                    </Switch>
                  </div>
                </CSSTransition>
              </TransitionGroup>
            </section>

            {authorized && (
              <ErrorBoundary
                render={() => renderNavigation}
                renderError={({ error }) => <AcNavigationError error={error} />}
              />
            )}

            {authorized && (
              <ErrorBoundary
                render={() => renderSupportCenter}
                renderError={({ error }) => (
                  <AcDefaultError
                    error={error}
                    component={"ac-support-center.web.js"}
                  />
                )}
              />
            )}

            {authorized && (
              <ErrorBoundary
                render={() => renderSubNavigation}
                renderError={({ error }) => (
                  <AcDefaultError
                    error={error}
                    component={"ac-sub-navigation.web.js"}
                  />
                )}
              />
            )}

            {authorized && renderBackdrop}

            {authorized && renderNotice}

            {authorized && (
              <Header
                query={fields.query}
                handleSearchInputChange={handleSearchInputChange}
                handleSearchClose={handleSearchClose}
                global={global}
                hasNotice={hasNotice}
              >
                <>
                  {" "}
                  <SupportMenuButton bubble={hasOpenTickets} />
                  <SubNavigationButton
                    current_merchant_profiles={current_merchant_profiles}
                  />
                </>
              </Header>
            )}

            {renderModal()}
            {renderConfirmModal()}
            {renderToasterHoc}

            {authorized && renderActivityMonitor}
          </main>
        </AcScrollHOC>
      </ErrorBoundary>
    </Suspense>
  );
};

export default withTranslation(["common", "titles", "store"])(
  withStore(observer(App))
);
